import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { RouteParam, ServiceBase } from '../service-base';
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { AgentSession } from 'src/app/@shared/models/ai/agent-session.model';
import { AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

const ROUTES: RouteParam[] = [
    { key: 'feedbackTurn', path: '{id}/turns/{turnIndex}/feedback/' },
    { key: 'undoTurn', path: '{id}/turns/{turnIndex}/undo/' },
];

/** Avis d'un tour tel que le serveur le renvoie après un dépôt ou un retrait (#1284). */
export interface AgentTurnFeedbackValue {
    feedback: AgentTurnFeedback | null;
    feedbackComment: string;
}

/** Résultat d'« Annuler cet échange » pour une action : `status` HTTP et messages de son annulation. */
export interface AgentTurnUndoResult {
    actionId: number;
    markerId: number | null;
    status: number;
    messages: ODataMessage[];
}

/**
 * Accès à l'historique des conversations de l'agent IA (#877).
 *
 * Réutilise ServiceBase : la liste tolère le tableau JSON simple renvoyé par
 * l'endpoint, le détail (avec ses `turns`) est désérialisé via la factory.
 * Les sessions sont créées par le chat (relay), jamais par ce service.
 */
@Injectable({
    providedIn: 'root'
})
export class AgentSessionService extends ServiceBase {

    constructor() {
        super('AgentSession', '/agent/sessions/');
        this.addRoutes(ROUTES);
    }

    /** Liste les conversations de l'utilisateur (triées par activité récente). */
    public list$(): Observable<AgentSession[]> {
        return this.getList$();
    }

    /** Ouvre une conversation : métadonnées + tours pour le rejeu (id = UUID). */
    public open$(id: string): Observable<AgentSession> {
        return this.get$(id as any, false);
    }

    /** Renomme une conversation (PATCH `{ title }`). */
    public rename$(session: AgentSession, title: string): Observable<AgentSession> {
        const url = this.routes.update.replace('{id}', session.sid);
        return this.httpClient.patch(url, { title }).pipe(
            map((body: any) => GeneralFactory.get('AgentSession', body?.value ?? body) as AgentSession),
            catchError(error => {
                this.handleError(error);
                return of(false as any);
            })
        );
    }

    /** Supprime une conversation (et ses tours). */
    public remove$(session: AgentSession): Observable<any> {
        session.deleted = true;
        return this.save$(session);
    }

    /**
     * « Annuler cet échange » (#1217) : défait ce que l'agent a été le premier à écrire pendant
     * le tour et retire ses propositions en attente. Toujours `200` : le bilan s'affiche,
     * et le détail dit, action par action, ce qui n'a pas pu être annulé.
     */
    public undoTurn$(sessionId: string, turnIndex: number): Observable<AgentTurnUndoResult[]> {
        const url = this.routes.undoTurn.replace('{id}', sessionId).replace('{turnIndex}', turnIndex.toString());
        return this._putActionValue$<any[]>(url).pipe(
            map(results => (results ?? []).map(result => ({
                actionId: result.actionId,
                markerId: result.markerId ?? null,
                status: result.status,
                messages: (result.messages ?? []).map((message: any) => new ODataMessage(message)),
            })))
        );
    }

    /**
     * Dépose, remplace ou retire (`feedback` à `null`) l'avis sur la réponse d'un tour (#1284).
     * Le tour n'existe côté serveur qu'à la fin du flux : à n'appeler qu'ensuite. Le pouce qui
     * s'allume est la confirmation, les messages de succès ne sont pas relayés.
     * Retourne `false` en cas d'échec (erreur déjà signalée par `handleError`).
     */
    public feedbackTurn$(sessionId: string, turnIndex: number, feedback: AgentTurnFeedback | null, feedbackComment: string = ''): Observable<AgentTurnFeedbackValue | false> {
        const url = this.routes.feedbackTurn.replace('{id}', sessionId).replace('{turnIndex}', turnIndex.toString());
        return this._putActionValue$<any>(url, { feedback, feedbackComment }, false).pipe(
            map(value => value ? { feedback: value.feedback ?? null, feedbackComment: value.feedbackComment ?? '' } : false)
        );
    }
}
